// The Arena's Find box: a forgiving, ranked search over every store on the list.
// "east bay furniture" finds "East Bay Furniture", "Eastbay Furniture Co" and "E. Bay
// Furniture Gallery"; "eastbay" finds "East Bay"; one wrong letter still finds the
// store; generic words (furniture, store, home...) never block a match on their own.

const GENERIC = new Set(['furniture', 'furnishings', 'furnishing', 'store', 'stores', 'shop', 'shoppe', 'mattress', 'mattresses', 'home', 'homes', 'gallery', 'galleries', 'outlet', 'outlets', 'warehouse', 'co', 'company', 'inc', 'llc', 'the', 'and', 'of', 'design', 'designs', 'interiors', 'interior', 'decor', 'center', 'centre', 'depot', 'mart', 'house', 'liquidation', 'liquidators', 'liquidator', 'discount', 'showroom', 'sales', 'more']);

export const normText = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
const squash = (s) => s.replace(/ /g, '');

/** Levenshtein distance, stopping early once it passes `max`. */
function editDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (cur[j] < best) best = cur[j];
    }
    if (best > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

// name first, notes last: a hit in the name outranks the same hit in a note
const FIELDS = [['name', 10], ['owner_name', 6], ['city', 5], ['state', 3], ['email', 3], ['address', 2], ['tags', 2], ['notes', 1]];

/** How well one term fits one field: 3 whole word, 2 word start, 1.5 inside, 1 one letter off, 0 not at all. */
function fit(term, field) {
  if (!field.text) return 0;
  if (field.words.includes(term)) return 3;
  if (field.words.some((w) => w.startsWith(term))) return 2;
  if (field.text.includes(term) || field.flat.includes(term)) return 1.5;
  if (term.length >= 4) {
    const max = term.length >= 8 ? 2 : 1;
    if (field.words.some((w) => w.length >= 3 && editDistance(term, w, max) <= max)) return 1;
  }
  return 0;
}

function indexRow(p) {
  const fields = FIELDS.map(([k, weight]) => {
    const raw = k === 'tags' ? (p.tags || []).join(' ') : k === 'state' ? String(p.state || '').trim() : p[k];
    const text = normText(raw);
    return { weight, text, words: text ? text.split(' ') : [], flat: squash(text) };
  });
  return { fields, digits: String(p.phone || '').replace(/\D/g, ''), flatName: fields[0].flat };
}

/**
 * Rank every store against the typed text. Returns { hits, partial }: hits are
 * the stores that fit every word (generic words may be missing), best first;
 * partial is true when nothing fit fully and the list is the closest stores
 * instead (every store that fits at least one real word on its name or owner).
 */
export function findStores(prospects, query, limit = 40) {
  const q = normText(query);
  if (!q) return { hits: [], partial: false };
  const terms = q.split(' ');
  const flatQ = squash(q);
  const digits = String(query || '').replace(/\D/g, '');
  const real = terms.filter((t) => !GENERIC.has(t));
  const full = [];
  const near = [];
  for (const p of prospects) {
    const row = indexRow(p);
    let score = 0;
    let matched = 0;
    let realMatched = 0;
    let realStrong = 0;
    for (const t of terms) {
      let best = 0;
      for (const f of row.fields) {
        const v = fit(t, f) * f.weight;
        if (v > best) best = v;
      }
      if (best > 0) {
        matched += 1;
        if (GENERIC.has(t)) score += best * 0.3; // "furniture" fitting "Furniture Country" must not outrank the real name
        else {
          score += best;
          realMatched += 1;
          if (best >= 20) realStrong += 1; // a whole word or word start on name, owner or city
        }
      }
    }
    // the whole phrase run together inside the name: "eastbay" ~ "east bay", "east bay furniture" ~ "Eastbay Furniture"
    if (flatQ.length >= 4 && row.flatName.includes(flatQ)) { score += 30; realMatched = real.length; matched = terms.length; realStrong = Math.max(realStrong, real.length); }
    const phoneHit = digits.length >= 4 && row.digits.includes(digits);
    if (phoneHit) { score += 40; realMatched = real.length; matched = terms.length; }
    const everyReal = real.length ? realMatched === real.length : matched === terms.length;
    if (everyReal) full.push({ p, score });
    else if (realStrong > 0) near.push({ p, score });
  }
  const sort = (a, b) => b.score - a.score || String(a.p.name || '').localeCompare(String(b.p.name || ''));
  if (full.length) return { hits: full.sort(sort).slice(0, limit).map((x) => x.p), partial: false };
  return { hits: near.sort(sort).slice(0, limit).map((x) => x.p), partial: near.length > 0 };
}
